import { UserPlusIcon } from 'lucide-react';
import { Formik } from 'formik';

import { CreateAccessValues } from '@/react/portainer/environments/types';
import { EnvironmentGroup } from '@/react/portainer/environments/environment-groups/types';
import { notifyError, notifySuccess } from '@/portainer/services/notifications';
import { FeatureId } from '@/react/portainer/feature-flags/enums';
import { useAccesses } from '@/react/portainer/environments/queries/useAccesses';
import { useUpdateGroupAccessMutation } from '@/react/portainer/environments/environment-groups/mutations/useUpdateGroupAccessMutation';
import { CreateAccessInnerForm } from '@/react/portainer/environments/AccessView/CreateAccessInnerForm';

import { Widget, WidgetBody, WidgetTitle } from '@@/Widget';
import { TextTip } from '@@/Tip/TextTip';
import { Alert } from '@@/Alert';

interface Props {
  group?: EnvironmentGroup;
  isLoading: boolean;
  onSuccess(): void;
}

export function CreateAccessWidget({ group, isLoading, onSuccess }: Props) {
  const {
    roles,
    hasPolicyOverride,
    availableUsersAndTeams,
    isLoading: isAccessesLoading,
    error: accessError,
  } = useAccesses(undefined, group);
  const updateMutation = useUpdateGroupAccessMutation();

  const initialValues: CreateAccessValues = {
    selectedUsersAndTeams: [],
    selectedRoleId: roles?.[0]?.Id,
  };

  if (hasPolicyOverride) {
    return null;
  }

  return (
    <Widget aria-label="Create access">
      <WidgetTitle icon={UserPlusIcon} title="Create access" />
      <WidgetBody>
        {accessError ? (
          <Alert color="error" title="Error loading access">
            {accessError.message ?? 'Unknown error'}
          </Alert>
        ) : (
          <>
            <TextTip className="mb-4" childrenWrapperClassName="text-warning">
              Adding user access will require the affected user(s) to logout and
              login for the changes to be taken into account.
            </TextTip>

            <Formik<CreateAccessValues>
              initialValues={initialValues}
              enableReinitialize
              onSubmit={handleSubmit}
              validateOnMount
            >
              {(formikProps) => (
                <CreateAccessInnerForm
                  values={formikProps.values}
                  setFieldValue={formikProps.setFieldValue}
                  isValid={formikProps.isValid}
                  dirty={formikProps.dirty}
                  availableUsersAndTeams={availableUsersAndTeams}
                  roles={roles}
                  isLoading={isLoading || isAccessesLoading}
                  isUpdating={updateMutation.isLoading}
                  limitedFeature={FeatureId.RBAC_ROLES}
                />
              )}
            </Formik>
          </>
        )}
      </WidgetBody>
    </Widget>
  );

  async function handleSubmit(
    values: CreateAccessValues,
    { resetForm }: { resetForm: () => void }
  ) {
    if (!group) {
      return;
    }
    try {
      const newUserPolicies = { ...group.UserAccessPolicies };
      const newTeamPolicies = { ...group.TeamAccessPolicies };

      values.selectedUsersAndTeams.forEach((access) => {
        if (access.Type === 'user') {
          newUserPolicies[access.Id] = { RoleId: values.selectedRoleId! };
        } else {
          newTeamPolicies[access.Id] = { RoleId: values.selectedRoleId! };
        }
      });

      await updateMutation.mutateAsync({
        id: group.Id,
        userAccessPolicies: newUserPolicies,
        teamAccessPolicies: newTeamPolicies,
      });

      notifySuccess('Success', 'Access successfully updated');
      resetForm();
      onSuccess();
    } catch (error) {
      notifyError('Failed to update access', error as Error);
    }
  }
}
